import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from '../contexts/ThemeContext';

const AboutPage = () => {
  const { theme } = useTheme();
  const [clickState, setClickState] = useState(0); // 0: center, 1: left, 2: far right, 3: center up
  const svgRef = useRef<SVGSVGElement>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);
  const [maskPosition, setMaskPosition] = useState({ cx: "50%", cy: "50%" });
  const [isMobile, setIsMobile] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [clickPulses, setClickPulses] = useState<Array<{ id: number; x: number; y: number }>>([]);

  useEffect(() => {
    if (svgRef.current && cursor.x !== null && cursor.y !== null) {
      const svgRect = svgRef.current.getBoundingClientRect();
      const cxPercentage = ((cursor.x - svgRect.left) / svgRect.width) * 100;
      const cyPercentage = ((cursor.y - svgRect.top) / svgRect.height) * 100;
      setMaskPosition({
        cx: `${cxPercentage}%`,
        cy: `${cyPercentage}%`,
      });
    }
  }, [cursor]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);
    updateIsMobile();
    mediaQuery.addEventListener('change', updateIsMobile);
    return () => mediaQuery.removeEventListener('change', updateIsMobile);
  }, []);

  const triggerDesktopPulse = (clientX: number, clientY: number) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setClickPulses((prev) => [...prev, { id, x: clientX, y: clientY }]);
  };

  const handleQuestionMarkClick = (e?: React.MouseEvent<HTMLDivElement | SVGSVGElement>) => {
    if (isMobile) {
      setClickState((prev) => (prev < 3 ? prev + 1 : prev));
      setIsModalOpen(true);
      return;
    }
    if (clickState === 0) {
      setClickState(1); // First click: move left, show first content
    } else if (clickState === 1) {
      setClickState(2); // Second click: move further right, show more content
    } else if (clickState === 2) {
      setClickState(3); // Third click: move to center up, show final content
    } else if (clickState === 3) {
      setClickState(0); // Fourth click: reset to beginning
    }
  };

  const handlePageClick = (e: React.MouseEvent) => {
    triggerDesktopPulse(e.clientX, e.clientY);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (isMobile && clickState === 3) {
      setClickState(0);
    }
  };

  const isLightTheme = theme === 'light';
  const aboutShellStyle = {
    background: isLightTheme
      ? 'radial-gradient(circle at 76% 8%, rgba(20,184,166,0.22), transparent 32%), radial-gradient(circle at 16% 78%, rgba(56,189,248,0.12), transparent 30%), linear-gradient(135deg, #f8fafc 0%, #eef7f8 48%, #f6fbfb 100%)'
      : 'radial-gradient(circle at 76% 8%, rgba(249,115,22,0.22), transparent 32%), radial-gradient(circle at 16% 78%, rgba(251,146,60,0.1), transparent 30%), linear-gradient(135deg, #050505 0%, #0b0b0d 50%, #17100b 100%)',
    ['--text-primary' as string]: isLightTheme ? '#082f49' : '#fff7ed',
    ['--text-secondary' as string]: isLightTheme ? 'rgba(8,47,73,0.68)' : 'rgba(255,237,213,0.68)',
    ['--accent-primary' as string]: isLightTheme ? '#0f766e' : '#f97316',
    ['--accent-secondary' as string]: isLightTheme ? '#38bdf8' : '#f59e0b',
    ['--card-bg' as string]: isLightTheme ? 'rgba(255,255,255,0.42)' : 'rgba(5,5,5,0.34)',
  };
  const panelGlassStyle = {
    background: isLightTheme ? 'rgba(255,255,255,0.42)' : 'rgba(5,5,5,0.34)',
    borderColor: isLightTheme ? 'rgba(20,184,166,0.28)' : 'rgba(251,146,60,0.26)',
    boxShadow: isLightTheme
      ? '0 0 54px rgba(20,184,166,0.14), inset 0 1px 0 rgba(255,255,255,0.42)'
      : '0 0 54px rgba(249,115,22,0.16), inset 0 1px 0 rgba(255,237,213,0.08)',
    backdropFilter: 'blur(22px)',
    WebkitBackdropFilter: 'blur(22px)',
  };
  const modalOverlayClass = isLightTheme
    ? 'md:hidden fixed inset-0 z-[200] flex items-center justify-center bg-sky-950/20 backdrop-blur-sm px-4 py-8'
    : 'md:hidden fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-8';
  const questionStroke = isLightTheme ? '#082f49' : '#fff7ed';
  const glowBaseColor = isLightTheme ? '20, 184, 166' : '249, 115, 22';

  const getQuestionMarkPosition = () => {
    if (clickState === 0) return { x: 0, y: 0 };
    if (clickState === 1) return { x: "-35vw", y: 0 };
    if (clickState === 2) return { x: "-35vw", y: 0 };
    if (clickState === 3) return { x: "-35vw", y: 0 };
    return { x: 0, y: 0 };
  };

  const renderFirstPanelContent = () => (
    <div className="max-w-3xl py-8">
      <motion.h1
        className="text-3xl md:text-5xl font-bold mb-6"
        style={{ background: 'linear-gradient(to right, var(--accent-primary), var(--accent-secondary))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.8 }}
      >
        About Us
      </motion.h1>

      <motion.div
        className="space-y-6"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.8 }}
      >
        <div>
          <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Who We Are</h2>
          <p className="text-base md:text-lg font-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
            <span style={{ color: 'var(--accent-primary)' }}>Design dot dev</span> is a collective of highly skilled professionals—developers, designers, and creative technologists—united by a passion for excellence and innovation. Our team brings together decades of experience from the world's most demanding industries and clients.
          </p>
        </div>

        <div>
          <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Development by Design Principles</h2>
          <p className="text-base md:text-lg font-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
            Every project at <span style={{ color: 'var(--accent-primary)' }}>Design dot dev</span> begins with a strong conceptual foundation. Our professional designers meticulously craft each product from the ground up, ensuring that every detail is intentional and every experience is unique. We believe that true innovation happens when design and development work hand-in-hand—transforming creative vision into digital reality, tailored for each customer's brand, goals, and audience.
          </p>
        </div>

        <div>
          <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Our Expertise</h2>
          <ul className="list-disc list-inside text-sm md:text-base font-medium space-y-1" style={{ color: 'var(--text-secondary)' }}>
            <li>20+ years of hands-on experience in web development, design, and digital strategy</li>
            <li>Cutting-edge coding, UI/UX, and creative direction</li>
            <li>Branding, corporate identity, and high-impact visual storytelling</li>
            <li>Enterprise-grade software, ERP systems, and realtime solutions</li>
            <li>SEO, performance optimization, and accessibility best practices</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>What Sets Us Apart</h2>
          <ul className="list-disc list-inside text-sm md:text-base font-medium space-y-1" style={{ color: 'var(--text-secondary)' }}>
            <li><span style={{ color: 'var(--accent-primary)' }}>Unmatched Detail:</span> Every pixel, every line of code, and every interaction is crafted with precision.</li>
            <li><span style={{ color: 'var(--accent-primary)' }}>Client-Centric Approach:</span> We listen, collaborate, and tailor solutions to your unique needs.</li>
            <li><span style={{ color: 'var(--accent-primary)' }}>Future-Proof Solutions:</span> Our work is built to last, scale, and adapt as your business grows.</li>
            <li><span style={{ color: 'var(--accent-primary)' }}>Full-Service Studio:</span> From concept to launch, we handle every aspect of your digital presence.</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );

  const renderSecondPanelContent = () => (
    <div className="max-w-3xl py-8">
      <motion.div
        className="space-y-6"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.8 }}
      >
        <div>
          <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Our Promise</h2>
          <p className="text-base md:text-lg font-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
            We don't just build websites and apps—we craft digital experiences that set new standards. Our commitment is simple:
          </p>
          <ul className="list-disc list-inside text-sm md:text-base font-medium space-y-1 mb-4" style={{ color: 'var(--text-secondary)' }}>
            <li>To make your product more professional, more impactful, and more successful than any studio can</li>
            <li>To deliver on time, on budget, and above expectations</li>
            <li>To be your long-term partner in digital excellence</li>
          </ul>
          <p className="text-base md:text-lg font-medium" style={{ color: 'var(--text-secondary)' }}>
            Partner with <span style={{ color: 'var(--accent-primary)' }}>Design dot dev</span> and discover what true digital mastery looks like.
          </p>
        </div>

        <div>
          <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Our Process</h2>
          <ul className="list-disc list-inside text-sm md:text-base font-medium space-y-1" style={{ color: 'var(--text-secondary)' }}>
            <li><span style={{ color: 'var(--accent-primary)' }}>Discovery & Strategy:</span> We dive deep into your business, goals, and target audience to create a tailored roadmap</li>
            <li><span style={{ color: 'var(--accent-primary)' }}>Design & Prototyping:</span> Our designers create stunning, user-centric interfaces that align with your brand identity</li>
            <li><span style={{ color: 'var(--accent-primary)' }}>Development & Testing:</span> Clean, efficient code built with the latest technologies and rigorous quality assurance</li>
            <li><span style={{ color: 'var(--accent-primary)' }}>Launch & Support:</span> Seamless deployment with ongoing maintenance and optimization support</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Technologies We Master</h2>
          <div className="grid grid-cols-2 gap-4 text-sm md:text-base font-medium" style={{ color: 'var(--text-secondary)' }}>
            <div>
              <h4 style={{ color: 'var(--accent-primary)' }} className="font-bold mb-2">Frontend</h4>
              <ul className="space-y-1">
                <li>React, Next.js, Vue.js</li>
                <li>TypeScript, JavaScript</li>
                <li>Tailwind CSS, Framer Motion</li>
                <li>Progressive Web Apps</li>
              </ul>
            </div>
            <div>
              <h4 style={{ color: 'var(--accent-primary)' }} className="font-bold mb-2">Backend</h4>
              <ul className="space-y-1">
                <li>Node.js, Python, PHP</li>
                <li>PostgreSQL, MongoDB</li>
                <li>GraphQL, REST APIs</li>
                <li>Cloud Infrastructure</li>
              </ul>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Why Choose Us?</h2>
          <p className="text-base md:text-lg font-medium" style={{ color: 'var(--text-secondary)' }}>
            In a world of cookie-cutter solutions, we stand apart. Every project is a masterpiece, every client relationship is a partnership, and every line of code is a step toward digital excellence. We don't just meet expectations—we exceed them, consistently delivering results that drive real business growth.
          </p>
        </div>
      </motion.div>
    </div>
  );

  const renderThirdPanelContent = () => (
    <div className="max-w-4xl py-8 text-center">
      <motion.div
        className="space-y-6"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.8 }}
      >
        <div>
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Hey there!</h2>
          <p className="text-lg md:text-xl font-medium mb-6 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            We know this can all feel a bit overwhelming at first. But here's the thing—you don't need to figure everything out on your own.
          </p>
          <p className="text-lg md:text-xl font-medium mb-6 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            Just tell us what you want to build, and we'll take care of the rest. Seriously, that's it! 
          </p>
          <p className="text-lg md:text-xl font-medium mb-6 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            Whether it's a simple website, a complex app, or something you haven't even dreamed up yet—we've got you covered. 
            We love turning wild ideas into reality, and we're pretty good at it too.
          </p>
          <p className="text-lg md:text-xl font-medium leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            So take a deep breath, grab a coffee, and let's build something amazing together. 
            <span style={{ color: 'var(--accent-primary)' }}> We're here to make this fun, not stressful.</span>
          </p>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div 
      className="min-h-screen w-full flex items-center justify-center relative overflow-hidden" 
      style={aboutShellStyle}
      onClick={handlePageClick}
    >
      {/* Question Mark with SVG Effects */}
      <motion.div
        className="relative z-10 cursor-pointer select-none"
        initial={{ x: 0 }}
        animate={isMobile ? { x: 0, y: 0 } : getQuestionMarkPosition()}
        transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
        onClick={(e) => handleQuestionMarkClick(e)}
        whileTap={clickState === 0 ? { scale: 0.95 } : {}}
      >
        <svg
          key={theme} // Force re-render when theme changes
          ref={svgRef}
          width="400"
          height="500"
          viewBox="0 0 400 500"
          xmlns="http://www.w3.org/2000/svg"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onMouseMove={(e) => setCursor({ x: e.clientX, y: e.clientY })}
          className="select-none w-56 h-72 sm:w-80 sm:h-96 md:w-[36rem] md:h-[45rem] lg:w-[48rem] lg:h-[60rem]">
          <defs>
            <linearGradient
              id="questionGradient"
              gradientUnits="userSpaceOnUse"
              cx="50%"
              cy="50%"
              r="25%">
              {hovered && (
                <>
                  {isLightTheme ? (
                    <>
                      <stop offset="0%" stopColor="#2dd4bf" />   {/* Teal */}
                      <stop offset="25%" stopColor="#14b8a6" />  {/* Darker teal */}
                      <stop offset="50%" stopColor="#0d9488" />  {/* Even darker teal */}
                      <stop offset="75%" stopColor="#ccfbf1" />  {/* Light teal */}
                      <stop offset="100%" stopColor="#f0fdfa" /> {/* Very light teal */}
                    </>
                  ) : (
                    <>
                      <stop offset="0%" stopColor="#f97316" />   {/* Orange */}
                      <stop offset="25%" stopColor="#f59e0b" />  {/* Amber */}
                      <stop offset="50%" stopColor="#eab308" />  {/* Yellow */}
                      <stop offset="75%" stopColor="#fde047" />  {/* Light yellow */}
                      <stop offset="100%" stopColor="#fef08a" /> {/* Very light yellow */}
                    </>
                  )}
                </>
              )}
            </linearGradient>

            <motion.radialGradient
              id="questionRevealMask"
              gradientUnits="userSpaceOnUse"
              r="10%"
              initial={{ cx: "50%", cy: "50%" }}
              animate={maskPosition}
              transition={{ duration: 0.3, ease: "easeOut" }}>
              <stop offset="0%" stopColor="white" />
              <stop offset="100%" stopColor="black" />
            </motion.radialGradient>
            <mask id="questionMask">
              <rect x="0" y="0" width="100%" height="100%" fill="url(#questionRevealMask)" />
            </mask>
          </defs>
          {/* Question Mark */}
          <text
            x="50%"
            y="35%"
            textAnchor="middle"
            dominantBaseline="middle"
            strokeWidth="2"
            className="fill-transparent font-[Raleway] text-9xl font-bold"
            style={{ 
              opacity: hovered ? 0.7 : 0,
              stroke: questionStroke
            }}>
            ?
          </text>
          <motion.text
            x="50%"
            y="35%"
            textAnchor="middle"
            dominantBaseline="middle"
            strokeWidth="2"
            className="fill-transparent font-[Raleway] text-9xl font-bold"
            style={{ stroke: questionStroke }}
            initial={{ strokeDashoffset: 1000, strokeDasharray: 1000 }}
            animate={{
              strokeDashoffset: 0,
              strokeDasharray: 1000,
            }}
            transition={{
              duration: 4,
              ease: "easeInOut",
            }}>
            ?
          </motion.text>
          <text
            x="50%"
            y="35%"
            textAnchor="middle"
            dominantBaseline="middle"
            stroke="url(#questionGradient)"
            strokeWidth="2"
            mask="url(#questionMask)"
            className="fill-transparent font-[Raleway] text-9xl font-bold">
            ?
          </text>

          {/* Dynamic Text */}
          <text
            x="50%"
            y="47.25%"
            textAnchor="middle"
            dominantBaseline="middle"
            strokeWidth="1"
            className="fill-transparent font-[Raleway] text-[2.2rem] font-bold"
            style={{ 
              opacity: hovered ? 0.7 : 0,
              stroke: questionStroke
            }}>
            {clickState === 0 ? "Hello, click me!" : clickState === 1 ? "Again!" : clickState === 2 ? "Last one!" : "Finale!"}
          </text>
          <motion.text
            x="50%"
            y="47.25%"
            textAnchor="middle"
            dominantBaseline="middle"
            strokeWidth="1"
            className="fill-transparent font-[Raleway] text-[2.2rem] font-bold"
            style={{ stroke: questionStroke }}
            initial={{ strokeDashoffset: 1000, strokeDasharray: 1000 }}
            animate={{
              strokeDashoffset: 0,
              strokeDasharray: 1000,
            }}
            transition={{
              duration: 4,
              ease: "easeInOut",
            }}>
            {clickState === 0 ? "Hello, click me!" : clickState === 1 ? "Again!" : clickState === 2 ? "Last one!" : "Finale!"}
          </motion.text>
          <text
            x="50%"
            y="47.25%"
            textAnchor="middle"
            dominantBaseline="middle"
            stroke="url(#questionGradient)"
            strokeWidth="1"
            mask="url(#questionMask)"
            className="fill-transparent font-[Raleway] text-[2.2rem] font-bold">
            {clickState === 0 ? "Hello, click me!" : clickState === 1 ? "Again!" : clickState === 2 ? "Last one!" : "Finale!"}
          </text>
        </svg>
      </motion.div>

      {/* First Content Panel (Right side) - Desktop/Tablet only */}
      <AnimatePresence>
        {clickState === 1 && (
          <motion.div
            className="hidden md:flex absolute right-0 top-0 h-full w-[70vw] flex-col justify-center px-8 md:px-16 z-0 overflow-y-auto border-l"
            style={panelGlassStyle}
            initial={{ x: "100%" }}
            animate={{ x: "0%" }}
            exit={{ x: "100%" }}
            transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
          >
            {renderFirstPanelContent()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Second Content Panel (Right side - same position as first panel) - Desktop/Tablet only */}
      <AnimatePresence>
        {clickState === 2 && (
          <motion.div
            className="hidden md:flex absolute right-0 top-0 h-full w-[70vw] flex-col justify-center px-8 md:px-16 z-0 overflow-y-auto border-l"
            style={panelGlassStyle}
            initial={{ x: "100%" }}
            animate={{ x: "0%" }}
            exit={{ x: "100%" }}
            transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
          >
            {renderSecondPanelContent()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Third Content Panel (Center under question mark) - Desktop/Tablet only */}
      <AnimatePresence>
        {clickState === 3 && (
          <motion.div
            className="hidden md:flex absolute h-auto w-[60vw] max-w-4xl flex-col justify-center px-8 md:px-16 z-0 rounded-lg border"
            style={{ 
              ...panelGlassStyle,
              left: '50%',
              top: '50%'
            }}
            initial={{ opacity: 0, y: '-45%', x: '-50%' }}
            animate={{ opacity: 1, y: '-75%', x: '-50%' }}
            exit={{ opacity: 0, y: '-45%', x: '-50%' }}
            transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
          >
            {renderThirdPanelContent()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Modal */}
      <AnimatePresence>
        {isMobile && isModalOpen && (
          <motion.div
            className={modalOverlayClass}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
              <motion.div
              className="relative w-full max-w-md rounded-xl shadow-xl overflow-hidden border"
              style={panelGlassStyle}
              initial={{ scale: 0.95, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 20, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            >
              <button
                aria-label="Close"
                onClick={handleCloseModal}
                className="absolute right-3 top-3 rounded-md text-base px-3 py-1.5 z-[210]"
                style={{ color: 'var(--text-primary)', backgroundColor: 'transparent' }}
              >
                ✕
              </button>
              <div className="p-6 max-h-[80vh] overflow-y-auto">
                {clickState === 1 && renderFirstPanelContent()}
                {clickState === 2 && renderSecondPanelContent()}
                {clickState === 3 && renderThirdPanelContent()}
                </div>
              </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click pulses at click position */}
      <AnimatePresence>
        {clickPulses.length > 0 && (
          <div className="pointer-events-none fixed inset-0 z-[170]">
            {clickPulses.map(({ id, x, y }) => (
              <motion.div
                key={id}
                className="absolute"
                style={{ top: y, left: x, transform: 'translate(-50%, -50%)' }}
                initial={{ opacity: 0.9 }}
                animate={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.4, ease: 'easeOut' }}
                onAnimationComplete={() =>
                  setClickPulses((prev) => prev.filter((p) => p.id !== id))
                }
              >
                <motion.div
                  aria-hidden
                  className="absolute rounded-full blur-3xl"
                  style={{
                    width: '26rem',
                    height: '26rem',
                    top: 'calc(50% - 13rem)',
                    left: 'calc(50% - 13rem)',
                    background: `radial-gradient(circle, rgba(${glowBaseColor},0.45) 0%, rgba(${glowBaseColor},0.25) 40%, rgba(${glowBaseColor},0.12) 60%, rgba(${glowBaseColor},0) 75%)`,
                  }}
                  animate={{
                    scale: [0.9, 1.05, 0.95, 1.25],
                    opacity: [0.7, 0.5, 0.7, 0.0],
                  }}
                  transition={{ duration: 1.4, ease: 'easeOut', times: [0, 0.35, 0.65, 1] }}
                />
                <motion.div
                  aria-hidden
                  className="absolute rounded-full"
                  style={{
                    top: 'calc(50% - 9rem)',
                    left: 'calc(50% - 9rem)',
                    width: '18rem',
                    height: '18rem',
                    border: `2px solid rgba(${glowBaseColor}, 0.8)`,
                    boxShadow: `0 0 40px 6px rgba(${glowBaseColor}, 0.35)`,
                  }}
                  animate={{
                    scale: [1, 1.25, 1.55],
                    opacity: [0.9, 0.6, 0],
                  }}
                  transition={{ duration: 1.4, ease: 'easeOut', times: [0, 0.6, 1] }}
                />
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AboutPage; 